import { reactive, watch, defineComponent, markRaw, getCurrentInstance, shallowRef, ref, provide, onMounted, unref, onBeforeUnmount, h, inject, createCommentVNode, Teleport, nextTick, warn, renderSlot, createTextVNode, isRef } from 'vue';
import { Map as Map$1, AttributionControl, FullscreenControl, GeolocateControl, NavigationControl, ScaleControl, Marker } from 'maplibre-gl';

const mapSymbol = Symbol('map'),
      isLoadedSymbol = Symbol('isLoaded'),
      componentIdSymbol = Symbol('componentId'),
      sourceIdSymbol = Symbol('sourceId'),
      sourceLayerRegistry = Symbol('sourceLayerRegistry'),
      emitterSymbol = Symbol('emitter');

const defaults = reactive({
  style: 'https://demotiles.maplibre.org/style.json',
  center: Object.freeze([0, 0]),
  zoom: 1,
  trackResize: false
});

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

class MapLib {
  static createEventHandler(component, map, ctx, eventName) {
    return (payload = {}) => ctx.emit(eventName, {
      type: payload.type,
      map,
      component,
      event: payload
    });
  }

}

_defineProperty(MapLib, "MAP_OPTION_KEYS", ['antialias', 'attributionControl', 'bearing', 'bearingSnap', 'bounds', 'boxZoom', 'center', 'clickTolerance', 'collectResourceTiming', 'crossSourceCollisions', 'container', 'customAttribution', 'dragPan', 'dragRotate', 'doubleClickZoom', 'hash', 'fadeDuration', 'failIfMajorPerformanceCaveat', 'fitBoundsOptions', 'interactive', 'keyboard', 'locale', 'localIdeographFontFamily', 'logoPosition', 'maxBounds', 'maxPitch', 'maxZoom', 'minPitch', 'minZoom', 'preserveDrawingBuffer', 'pitch', 'pitchWithRotate', 'refreshExpiredTiles', 'renderWorldCopies', 'scrollZoom', 'mapStyle', 'trackResize', 'transformRequest', 'touchZoomRotate', 'touchPitch', 'zoom', 'maxTileCacheSize', 'accessToken']);

_defineProperty(MapLib, "MARKER_OPTION_KEYS", ['element', 'offset', 'anchor', 'color', 'draggable', 'clickTolerance', 'rotation', 'rotationAlignment', 'pitchAlignment', 'scale']);

_defineProperty(MapLib, "MAP_EVENT_TYPES", ['error', 'load', 'idle', 'remove', 'render', 'resize', 'webglcontextlost', 'webglcontextrestored', 'dataloading', 'data', 'tiledataloading', 'sourcedataloading', 'styledataloading', 'sourcedata', 'styledata', 'boxzoomcancel', 'boxzoomstart', 'boxzoomend', 'touchcancel', 'touchmove', 'touchend', 'touchstart', 'click', 'contextmenu', 'dblclick', 'mousemove', 'mouseup', 'mousedown', 'mouseout', 'mouseover', 'movestart', 'move', 'moveend', 'zoomstart', 'zoom', 'zoomend', 'rotatestart', 'rotate', 'rotateend', 'dragstart', 'drag', 'dragend', 'pitchstart', 'pitch', 'pitchend', 'wheel']);

var Position;

(function (Position) {
  Position["TOP_LEFT"] = "top-left";
  Position["TOP_RIGHT"] = "top-right";
  Position["BOTTOM_LEFT"] = "bottom-left";
  Position["BOTTOM_RIGHT"] = "bottom-right";
})(Position || (Position = {}));

const PositionValues = Object.values(Position);
function usePositionWatcher(source, map, control) {
  watch(source, v => {
    if (v && PositionValues.indexOf(v) === -1) {
      return;
    }

    if (map.value.hasControl(control)) {
      map.value.removeControl(control);
    }

    map.value.addControl(control, v);
  }, {
    immediate: true
  });
}

function mitt(n){return {all:n=n||new Map,on:function(t,e){var i=n.get(t);i?i.push(e):n.set(t,[e]);},off:function(t,e){var i=n.get(t);i&&(e?i.splice(i.indexOf(e)>>>0,1):n.set(t,[]));},emit:function(t,e){var i=n.get(t);i&&i.slice().map(function(n){n(e);}),(i=n.get("*"))&&i.slice().map(function(n){n(t,e);});}}}

const instances = new Map(),
      defaultKey = Symbol('default'); // useMap returns reactive version of MapInstance

function useMap(key = defaultKey) {
  let component = instances.get(key);

  if (!component) {
    component = reactive({
      isLoaded: false,
      isMounted: false
    });
    instances.set(key, component);
  }

  return component;
}
function registerMap(instance, key = defaultKey) {
  var _instance$map;

  let component = instances.get(key);

  if (!component) {
    component = reactive({
      isLoaded: false,
      isMounted: false
    });
    instances.set(key, component);
  }

  component.isLoaded = false;
  component.isMounted = false;
  component.component = instance;
  component.map = instance.map;
  component.isLoaded = ((_instance$map = instance.map) === null || _instance$map === void 0 ? void 0 : _instance$map.loaded()) || false;
  return component;
}

function debounce(fn, wait = 250, immediate = false) {
  let timeout;

  function debounced() {
    const args = arguments;

    const later = () => {
      timeout = undefined;

      if (immediate !== true) {
        // @ts-ignore
        fn.apply(this, args);
      }
    };

    clearTimeout(timeout);

    if (immediate === true && timeout === undefined) {
      // @ts-ignore
      fn.apply(this, args);
    }

    timeout = window.setTimeout(later, wait);
  }

  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return debounced;
}

var map_component = defineComponent({
  name: 'MglMap',
  props: {
    width: {
      type: [Number, String],
      default: '100%'
    },
    height: {
      type: [Number, String],
      default: '100%'
    },
    antialias: {
      type: Boolean,
      default: () => defaults.antialias
    },
    attributionControl: {
      type: Boolean,
      default: () => defaults.attributionControl
    },
    bearing: {
      type: Number,
      default: () => defaults.bearing
    },
    bearingSnap: {
      type: Number,
      default: () => defaults.bearingSnap
    },
    bounds: {
      type: [Array, Object],
      default: () => defaults.bounds
    },
    boxZoom: {
      type: Boolean,
      default: () => defaults.boxZoom
    },
    center: {
      type: [Array, Object],
      default: () => defaults.center
    },
    clickTolerance: {
      type: Number,
      default: () => defaults.clickTolerance
    },
    collectResourceTiming: {
      type: Boolean,
      default: () => defaults.collectResourceTiming
    },
    crossSourceCollisions: {
      type: Boolean,
      default: () => defaults.crossSourceCollisions
    },
    customAttribution: {
      type: [String, Array],
      default: () => defaults.customAttribution
    },
    dragPan: {
      type: Boolean,
      default: () => defaults.dragPan
    },
    dragRotate: {
      type: Boolean,
      default: () => defaults.dragRotate
    },
    doubleClickZoom: {
      type: Boolean,
      default: () => defaults.doubleClickZoom
    },
    hash: {
      type: [Boolean, String],
      default: () => defaults.hash
    },
    fadeDuration: {
      type: Number,
      default: () => defaults.fadeDuration
    },
    failIfMajorPerformanceCaveat: {
      type: Boolean,
      default: () => defaults.failIfMajorPerformanceCaveat
    },
    fitBoundsOptions: {
      type: Object,
      default: () => defaults.fitBoundsOptions
    },
    interactive: {
      type: Boolean,
      default: () => defaults.interactive
    },
    keyboard: {
      type: Boolean,
      default: () => defaults.keyboard
    },
    locale: {
      type: Object,
      default: () => defaults.locale
    },
    localIdeographFontFamily: {
      type: String,
      default: () => defaults.localIdeographFontFamily
    },
    logoPosition: {
      type: [String],
      validate: val => val in Position,
      default: () => defaults.logoPosition
    },
    maxBounds: {
      type: [Array, Object],
      default: () => defaults.maxBounds
    },
    maxPitch: {
      type: Number,
      default: () => defaults.maxPitch
    },
    maxZoom: {
      type: Number,
      default: () => defaults.maxZoom
    },
    minPitch: {
      type: Number,
      default: () => defaults.minPitch
    },
    minZoom: {
      type: Number,
      default: () => defaults.minZoom
    },
    preserveDrawingBuffer: {
      type: Boolean,
      default: () => defaults.preserveDrawingBuffer
    },
    pitch: {
      type: Number,
      default: () => defaults.pitch
    },
    pitchWithRotate: {
      type: Boolean,
      default: () => defaults.pitchWithRotate
    },
    refreshExpiredTiles: {
      type: Boolean,
      default: () => defaults.refreshExpiredTiles
    },
    renderWorldCopies: {
      type: Boolean,
      default: () => defaults.renderWorldCopies
    },
    scrollZoom: {
      type: Boolean,
      default: () => defaults.scrollZoom
    },
    mapStyle: {
      type: [String, Object],
      default: () => defaults.style
    },
    trackResize: {
      type: Boolean,
      default: () => defaults.trackResize
    },
    transformRequest: {
      type: Function,
      default: defaults.transformRequest
    },
    touchZoomRotate: {
      type: Boolean,
      default: () => defaults.touchZoomRotate
    },
    touchPitch: {
      type: Boolean,
      default: () => defaults.touchPitch
    },
    zoom: {
      type: Number,
      default: () => defaults.zoom
    },
    maxTileCacheSize: {
      type: Number,
      default: () => defaults.maxTileCacheSize
    },
    accessToken: {
      type: String,
      default: () => defaults.accessToken
    },
    mapKey: {
      type: [String, Symbol]
    }
  },
  // emits: [],
  emits: ['map:error', 'map:load', 'map:idle', 'map:remove', 'map:render', 'map:resize', 'map:webglcontextlost', 'map:webglcontextrestored', 'map:dataloading', 'map:data', 'map:tiledataloading', 'map:sourcedataloading', 'map:styledataloading', 'map:sourcedata', 'map:styledata', 'map:boxzoomcancel', 'map:boxzoomstart', 'map:boxzoomend', 'map:touchcancel', 'map:touchmove', 'map:touchend', 'map:touchstart', 'map:click', 'map:contextmenu', 'map:dblclick', 'map:mousemove', 'map:mouseup', 'map:mousedown', 'map:mouseout', 'map:mouseover', 'map:movestart', 'map:move', 'map:moveend', 'map:zoomstart', 'map:zoom', 'map:zoomend', 'map:rotatestart', 'map:rotate', 'map:rotateend', 'map:dragstart', 'map:drag', 'map:dragend', 'map:pitchstart', 'map:pitch', 'map:pitchend', 'map:wheel'],

  setup(props, ctx) {
    const component = markRaw(getCurrentInstance()),
          componentContainer = shallowRef(null),
          container = shallowRef(null),
          map = shallowRef(null),
          isInitialized = ref(false),
          isLoaded = ref(false),
          boundMapEvents = new Map(),
          emitter = mitt(),
          registryItem = registerMap(component, props.mapKey);
    let resizeObserver;
    provide(mapSymbol, map);
    provide(isLoadedSymbol, isLoaded);
    provide(componentIdSymbol, component.uid);
    provide(sourceIdSymbol, '');
    provide(emitterSymbol, emitter);
    /*
     * bind prop watchers
     */

    watch(() => props.bearing, v => {
      if (v) {
        var _map$value;

        (_map$value = map.value) === null || _map$value === void 0 ? void 0 : _map$value.setBearing(v);
      }
    });
    watch(() => props.bounds, v => {
      if (v) {
        var _map$value2;

        (_map$value2 = map.value) === null || _map$value2 === void 0 ? void 0 : _map$value2.fitBounds(v, props.fitBoundsOptions);
      }
    });
    watch(() => props.center, v => {
      if (v) {
        var _map$value3;

        (_map$value3 = map.value) === null || _map$value3 === void 0 ? void 0 : _map$value3.setCenter(v);
      }
    });
    watch(() => props.maxBounds, v => {
      if (v) {
        var _map$value4;

        (_map$value4 = map.value) === null || _map$value4 === void 0 ? void 0 : _map$value4.setMaxBounds(v);
      }
    });
    watch(() => props.maxPitch, v => {
      if (v) {
        var _map$value5;

        (_map$value5 = map.value) === null || _map$value5 === void 0 ? void 0 : _map$value5.setMaxPitch(v);
      }
    });
    watch(() => props.maxZoom, v => {
      if (v) {
        var _map$value6;

        (_map$value6 = map.value) === null || _map$value6 === void 0 ? void 0 : _map$value6.setMaxZoom(v);
      }
    });
    watch(() => props.minPitch, v => {
      if (v) {
        var _map$value7;

        (_map$value7 = map.value) === null || _map$value7 === void 0 ? void 0 : _map$value7.setMinPitch(v);
      }
    });
    watch(() => props.minZoom, v => {
      if (v) {
        var _map$value8;

        (_map$value8 = map.value) === null || _map$value8 === void 0 ? void 0 : _map$value8.setMinZoom(v);
      }
    });
    watch(() => props.pitch, v => {
      if (v) {
        var _map$value9;

        (_map$value9 = map.value) === null || _map$value9 === void 0 ? void 0 : _map$value9.setPitch(v);
      }
    });
    watch(() => props.renderWorldCopies, v => {
      if (v) {
        var _map$value10;

        (_map$value10 = map.value) === null || _map$value10 === void 0 ? void 0 : _map$value10.setRenderWorldCopies(v);
      }
    });
    watch(() => props.mapStyle, v => {
      if (v) {
        var _map$value11;

        (_map$value11 = map.value) === null || _map$value11 === void 0 ? void 0 : _map$value11.setStyle(v);
      }
    });
    watch(() => props.transformRequest, v => {
      if (v) {
        var _map$value12;

        (_map$value12 = map.value) === null || _map$value12 === void 0 ? void 0 : _map$value12.setTransformRequest(v);
      }
    });
    watch(() => props.zoom, v => {
      if (v) {
        var _map$value13;

        (_map$value13 = map.value) === null || _map$value13 === void 0 ? void 0 : _map$value13.setZoom(v);
      }
    });
    watch(() => props.zoom, v => {
      if (v) {
        var _map$value14;

        (_map$value14 = map.value) === null || _map$value14 === void 0 ? void 0 : _map$value14.setZoom(v);
      }
    });
    /*
     * init map
     */

    onMounted(() => {
      registryItem.isMounted = true; // build options

      const opts = Object.keys(props).filter(opt => props[opt] !== undefined && MapLib.MAP_OPTION_KEYS.indexOf(opt) !== -1).reduce((obj, opt) => {
        obj[opt === 'mapStyle' ? 'style' : opt] = unref(props[opt]);
        return obj;
      }, {
        container: container.value
      }); // init map
      // @ts-ignore

      map.value = markRaw(new Map$1(opts));
      registryItem.map = map.value;
      isInitialized.value = true;
      boundMapEvents.set('__load', () => (isLoaded.value = true, registryItem.isLoaded = true));
      map.value.on('load', boundMapEvents.get('__load')); // bind events

      if (component.vnode.props) {
        for (let i = 0, len = MapLib.MAP_EVENT_TYPES.length; i < len; i++) {
          if (component.vnode.props['onMap:' + MapLib.MAP_EVENT_TYPES[i]]) {
            const handler = MapLib.createEventHandler(component, map.value, ctx, 'map:' + MapLib.MAP_EVENT_TYPES[i]);
            boundMapEvents.set(MapLib.MAP_EVENT_TYPES[i], handler);
            map.value.on(MapLib.MAP_EVENT_TYPES[i], handler);
          }
        }
      } // bind resize observer


      resizeObserver = new ResizeObserver(debounce(map.value.resize.bind(map.value), 100));
      resizeObserver.observe(container.value);
    });
    /*
     * Dispose component
     */

    onBeforeUnmount(() => {
      registryItem.isMounted = false;
      registryItem.isLoaded = false; // unbind resize observer

      if (resizeObserver) {
        resizeObserver.disconnect();
      } // unbind events


      if (map.value) {
        boundMapEvents.forEach((func, en) => {
          map.value.off(en.startsWith('__') ? en.substr(2) : en, func);
        });
      }
    });
    return {
      map,
      componentContainer,
      container,
      isLoaded,
      isInitialized
    };
  },

  render() {
    return h('div', {
      ref: 'componentContainer',
      'class': 'mgl-container',
      style: {
        height: this.$props.height,
        width: this.$props.width
      }
    }, [h('div', {
      ref: 'container',
      'class': 'mgl-wrapper'
    }), this.isInitialized && this.$slots.default ? this.$slots.default() : undefined]);
  }

});

var attribution_control = defineComponent({
  name: 'MglAttributionControl',
  props: {
    position: {
      type: String,
      validator: v => {
        return PositionValues.indexOf(v) !== -1;
      }
    },
    compact: Boolean,
    customAttribution: [String, Array]
  },

  setup(props) {
    const map = inject(mapSymbol),
          control = new AttributionControl({
      compact: props.compact,
      customAttribution: props.customAttribution
    });
    usePositionWatcher(() => props.position, map, control);
    onBeforeUnmount(() => map.value.removeControl(control));
  },

  render() {// nothing
  }

});

class CustomControl {
  constructor(isAdded, noClasses) {
    this.isAdded = isAdded;
    this.container = document.createElement('div');
    this.setClasses(noClasses);
  }

  getDefaultPosition() {
    return Position.TOP_LEFT;
  }

  onAdd() {
    nextTick(() => this.isAdded.value = true);
    return this.container;
  }

  onRemove() {
    this.isAdded.value = false;
    this.container.remove();
  }

  setClasses(noClasses) {
    if (noClasses) {
      this.container.classList.remove(CustomControl.CONTROL_CLASS);
      this.container.classList.remove(CustomControl.CONTROL_GROUP_CLASS);
    } else {
      this.container.classList.add(CustomControl.CONTROL_CLASS);
      this.container.classList.add(CustomControl.CONTROL_GROUP_CLASS);
    }
  }

}

_defineProperty(CustomControl, "CONTROL_CLASS", 'maplibregl-ctrl');

_defineProperty(CustomControl, "CONTROL_GROUP_CLASS", 'maplibregl-ctrl-group');

var custom_control = defineComponent({
  name: 'MglCustomControl',
  props: {
    position: {
      type: String,
      validator: v => {
        return PositionValues.indexOf(v) !== -1;
      }
    },
    noClasses: {
      type: Boolean,
      default: false
    }
  },

  setup(props) {
    const map = inject(mapSymbol),
          isAdded = ref(false),
          control = new CustomControl(isAdded, props.noClasses);
    usePositionWatcher(() => props.position, map, control);
    watch(() => props.noClasses, v => control.setClasses(v));
    onBeforeUnmount(() => {
      map.value.removeControl(control);
    });
    return {
      isAdded,
      container: control.container
    };
  },

  render() {
    if (!this.isAdded) {
      return createCommentVNode('custom-component');
    }

    return h(Teleport, {
      to: this.container
    }, this.$slots.default ? this.$slots.default() : undefined);
  }

});

var fullscreen_control = defineComponent({
  name: 'MglFullscreenControl',
  props: {
    position: {
      type: String,
      default: Position.TOP_RIGHT,
      validator: v => {
        return PositionValues.indexOf(v) !== -1;
      }
    },
    container: {
      type: HTMLElement,
      default: null
    }
  },

  setup(props) {
    const map = inject(mapSymbol),
          control = new FullscreenControl(props.container ? {
      container: props.container
    } : undefined);
    usePositionWatcher(() => props.position, map, control);
    onBeforeUnmount(() => map.value.removeControl(control));
  },

  render() {// nothing
  }

});

class FrameRateControl {
  constructor(background = 'rgba(0,0,0,0.9)', barWidth = 4 * window.devicePixelRatio, color = '#7cf859', font = 'Monaco, Consolas, Courier, monospace', graphHeight = 60 * window.devicePixelRatio, graphWidth = 90 * window.devicePixelRatio, graphTop = 0, graphRight = 5 * window.devicePixelRatio, width = 100 * window.devicePixelRatio) {
    _defineProperty(this, "frames", 0);

    _defineProperty(this, "totalTime", 0);

    _defineProperty(this, "totalFrames", 0);

    _defineProperty(this, "time", null);

    _defineProperty(this, "eventHandlers", new Map());

    this.background = background;
    this.barWidth = barWidth;
    this.color = color;
    this.font = font;
    this.graphHeight = graphHeight;
    this.graphWidth = graphWidth;
    this.graphTop = graphTop;
    this.graphRight = graphRight;
    this.width = width;
  }

  getDefaultPosition() {
    return Position.TOP_RIGHT;
  }

  onAdd(map) {
    this.map = map;
    const el = this.container = document.createElement('div');
    el.className = 'mapboxgl-ctrl mapboxgl-ctrl-fps';
    el.style.backgroundColor = this.background;
    el.style.borderRadius = '6px';
    this.readOutput = document.createElement('div');
    this.readOutput.style.color = this.color;
    this.readOutput.style.fontFamily = this.font;
    this.readOutput.style.padding = '0 5px 5px';
    this.readOutput.style.fontSize = '9px';
    this.readOutput.style.fontWeight = 'bold';
    this.readOutput.textContent = 'Waiting…';
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'mapboxgl-ctrl-canvas';
    this.canvas.width = this.width;
    this.canvas.height = this.graphHeight;
    this.canvas.style.cssText = `width: ${this.width / window.devicePixelRatio}px; height: ${this.graphHeight / window.devicePixelRatio}px;`;
    el.appendChild(this.readOutput);
    el.appendChild(this.canvas);
    this.eventHandlers.set('movestart', this.onMoveStart.bind(this));
    this.eventHandlers.set('moveend', this.onMoveEnd.bind(this));
    this.map.on('movestart', this.eventHandlers.get('movestart'));
    this.map.on('moveend', this.eventHandlers.get('moveend'));
    return this.container;
  }

  onRemove() {
    this.map.off('movestart', this.eventHandlers.get('movestart'));
    this.map.off('moveend', this.eventHandlers.get('moveend'));
    this.eventHandlers.clear();
    this.container.parentNode.removeChild(this.container);
    this.map = undefined;
  }

  onMoveStart() {
    this.frames = 0;
    this.time = performance.now();
    this.eventHandlers.set('render', this.onRender.bind(this));
    this.map.on('render', this.eventHandlers.get('render'));
  }

  onMoveEnd() {
    const now = performance.now();
    this.updateGraph(this.getFPS(now));
    this.frames = 0;
    this.time = null;
    this.map.off('render', this.eventHandlers.get('render'));
  }

  onRender() {
    if (this.time) {
      this.frames++;
      const now = performance.now();

      if (now >= this.time + 1e3) {
        this.updateGraph(this.getFPS(now));
        this.frames = 0;
        this.time = performance.now();
      }
    }
  }

  getFPS(now) {
    this.totalTime += now - this.time;
    this.totalFrames += this.frames;
    return Math.round(1e3 * this.frames / (now - this.time)) || 0;
  }

  updateGraph(fpsNow) {
    const context = this.canvas.getContext('2d');
    const fps = Math.round(1e3 * this.totalFrames / this.totalTime) || 0;
    const rect = (this.graphHeight, this.barWidth);
    context.fillStyle = this.background;
    context.globalAlpha = 1;
    context.fillRect(0, 0, this.graphWidth, this.graphTop);
    context.fillStyle = this.color;
    this.readOutput.textContent = `${fpsNow} FPS (${fps} Avg)`;
    context.drawImage(this.canvas, this.graphRight + rect, this.graphTop, this.graphWidth - rect, this.graphHeight, this.graphRight, this.graphTop, this.graphWidth - rect, this.graphHeight);
    context.fillRect(this.graphRight + this.graphWidth - rect, this.graphTop, rect, this.graphHeight);
    context.fillStyle = this.background;
    context.globalAlpha = 0.75;
    context.fillRect(this.graphRight + this.graphWidth - rect, this.graphTop, rect, (1 - fpsNow / 100) * this.graphHeight);
  }

}
var frameRate_control = defineComponent({
  name: 'MglFrameRateControl',
  props: {
    position: {
      type: String,
      validator: v => {
        return PositionValues.indexOf(v) !== -1;
      }
    },
    background: {
      type: String,
      default: 'rgba(0,0,0,0.9)'
    },
    barWidth: {
      type: Number,
      default: 4 * window.devicePixelRatio
    },
    color: {
      type: String,
      default: '#7cf859'
    },
    font: {
      type: String,
      default: 'Monaco, Consolas, Courier, monospace'
    },
    graphHeight: {
      type: Number,
      default: 60 * window.devicePixelRatio
    },
    graphWidth: {
      type: Number,
      default: 90 * window.devicePixelRatio
    },
    graphTop: {
      type: Number,
      default: 0
    },
    graphRight: {
      type: Number,
      default: 5 * window.devicePixelRatio
    },
    width: {
      type: Number,
      default: 100 * window.devicePixelRatio
    }
  },

  setup(props) {
    const map = inject(mapSymbol),
          control = new FrameRateControl(props.background, props.barWidth, props.color, props.font, props.graphHeight, props.graphWidth, props.graphTop, props.graphRight, props.width);
    usePositionWatcher(() => props.position, map, control);
    onBeforeUnmount(() => map.value.removeControl(control));
  },

  render() {// nothing
  }

});

var geolocation_control = defineComponent({
  name: 'MglGeolocationControl',
  props: {
    position: {
      type: String,
      default: Position.TOP_RIGHT,
      validator: v => {
        return PositionValues.indexOf(v) !== -1;
      }
    },
    positionOptions: {
      type: Object,
      default: {
        enableHighAccuracy: false,
        timeout: 6000
      }
    },
    fitBoundsOptions: {
      type: Object,
      default: {
        maxZoom: 15
      }
    },
    trackUserLocation: {
      type: Boolean,
      default: false
    },
    showAccuracyCircle: {
      type: Boolean,
      default: true
    },
    showUserLocation: {
      type: Boolean,
      default: true
    }
  },

  setup(props) {
    const map = inject(mapSymbol),
          control = new GeolocateControl({
      positionOptions: props.positionOptions,
      fitBoundsOptions: props.fitBoundsOptions,
      trackUserLocation: props.trackUserLocation,
      showAccuracyCircle: props.showAccuracyCircle,
      showUserLocation: props.showUserLocation
    });
    usePositionWatcher(() => props.position, map, control);
    onBeforeUnmount(() => map.value.removeControl(control));
  },

  render() {// nothing
  }

});

var navigation_control = defineComponent({
  name: 'MglNavigationControl',
  props: {
    position: {
      type: String,
      default: Position.TOP_RIGHT,
      validator: v => {
        return PositionValues.indexOf(v) !== -1;
      }
    },
    showCompass: {
      type: Boolean,
      default: true
    },
    showZoom: {
      type: Boolean,
      default: true
    },
    visualizePitch: Boolean
  },

  setup(props) {
    const map = inject(mapSymbol),
          control = new NavigationControl({
      showCompass: props.showCompass,
      showZoom: props.showZoom,
      visualizePitch: props.visualizePitch
    });
    usePositionWatcher(() => props.position, map, control);
    onBeforeUnmount(() => map.value.removeControl(control));
  },

  render() {// nothing
  }

});

var ScaleControlUnit;

(function (ScaleControlUnit) {
  ScaleControlUnit["IMPERIAL"] = "imperial";
  ScaleControlUnit["METRIC"] = "metric";
  ScaleControlUnit["NAUTICAL"] = "nautical";
})(ScaleControlUnit || (ScaleControlUnit = {}));

const UnitValues = Object.values(ScaleControlUnit);
var scale_control = defineComponent({
  name: 'MglScaleControl',
  props: {
    position: {
      type: String,
      validator: v => {
        return PositionValues.indexOf(v) !== -1;
      }
    },
    maxWidth: {
      type: Number,
      default: 100
    },
    unit: {
      type: String,
      default: ScaleControlUnit.METRIC,
      validator: v => {
        return UnitValues.indexOf(v) !== -1;
      }
    }
  },

  setup(props) {
    const map = inject(mapSymbol),
          control = new ScaleControl({
      maxWidth: props.maxWidth,
      unit: props.unit
    });
    usePositionWatcher(() => props.position, map, control);
    onBeforeUnmount(() => map.value.removeControl(control));
  },

  render() {// nothing
  }

});

var ButtonType;

(function (ButtonType) {
  ButtonType["DEFAULT"] = "default";
  ButtonType["TEXT"] = "text";
  ButtonType["MDI"] = "mdi";
  ButtonType["SIMPLE_ICON"] = "simple-icons";
})(ButtonType || (ButtonType = {}));

const ButtonTypeValues = Object.values(ButtonType);
const types = {
  [ButtonType.TEXT]: undefined,
  [ButtonType.MDI]: {
    size: 21,
    viewbox: '0 0 24 24'
  },
  [ButtonType.SIMPLE_ICON]: {
    size: 21,
    viewbox: '0 0 24 24'
  },
  [ButtonType.DEFAULT]: {
    size: 0,
    viewbox: '0 0 0 0'
  }
};
var MglButton = defineComponent({
  name: 'MglButton',
  props: {
    type: {
      type: String,
      default: ButtonType.DEFAULT,
      validator: v => {
        return ButtonTypeValues.indexOf(v) !== -1;
      }
    },
    path: {
      type: String
    },
    size: Number,
    viewbox: String
  },

  setup(props) {
    if (!props.path && props.type !== ButtonType.TEXT) {
      warn('property `path` must be set on MaplibreButton');
    }

    const defaults = ref(types[props.type] || types.default);
    watch(() => props.type, v => defaults.value = types[v] || types.default);
    return {
      defaults
    };
  },

  render() {
    if (this.type === ButtonType.TEXT) {
      return h('button', {
        type: 'button'
      }, this.$slots.default ? this.$slots.default() : undefined);
    }

    return h('button', {
      type: 'button',
      'class': 'maplibregl-ctrl-icon'
    }, [h('svg', {
      width: this.size || this.defaults.size,
      height: this.size || this.defaults.size,
      viewBox: this.viewbox || this.defaults.viewbox
    }, h('path', {
      fill: 'currentColor',
      d: this.path
    })), this.$slots.default ? this.$slots.default() : undefined]);
  }

});

// Material Design Icons v6.1.95
var mdiLayersOutline = "M12,18.54L19.37,12.8L21,14.07L12,21.07L3,14.07L4.62,12.81L12,18.54M12,16L3,9L12,2L21,9L12,16M12,4.53L6.26,9L12,13.47L17.74,9L12,4.53Z";

function isEvent(e) {
  return e && !!e.stopPropagation;
}

var styleSwitch_control = defineComponent({
  name: 'MglStyleSwitchControl',
  props: {
    position: {
      type: String,
      validator: v => {
        return PositionValues.indexOf(v) !== -1;
      }
    },
    mapStyles: {
      type: Array,
      required: true,
      default: []
    },
    modelValue: {
      type: Object
    },
    isOpen: {
      type: Boolean,
      default: undefined
    }
  },
  emits: ['update:modelValue', 'update:isOpen'],

  setup(props, ctx) {
    const map = inject(mapSymbol),
          isMapLoaded = inject(isLoadedSymbol),
          emitter = inject(emitterSymbol),
          isAdded = ref(false),
          isOpen = ref(props.isOpen === undefined ? false : props.isOpen),
          modelValue = ref(props.modelValue === undefined ? props.mapStyles.length ? props.mapStyles[0] : null : props.modelValue),
          control = new CustomControl(isAdded, false),
          closer = toggleOpen.bind(null, false);

    function setStyleByMap() {
      const name = map.value.getStyle().name;

      for (let i = 0, len = props.mapStyles.length; i < len; i++) {
        if (props.mapStyles[i].name === name) {
          setStyle(props.mapStyles[i]);
          break;
        }
      }
    }

    watch(isMapLoaded, v => {
      if (v) setStyleByMap();
    }, {
      immediate: true
    });
    map.value.on('style.load', setStyleByMap);
    document.addEventListener('click', closer);
    usePositionWatcher(() => props.position, map, control);

    if (props.modelValue !== undefined) {
      watch(() => props.modelValue, v => {
        if (v !== undefined) modelValue.value = v;
      });
    }

    if (props.isOpen !== undefined) {
      watch(() => props.isOpen, v => {
        if (v !== undefined) isOpen.value = v;
      });
    }

    onBeforeUnmount(() => {
      map.value.removeControl(control);
      map.value.off('style.load', setStyleByMap);
      document.removeEventListener('click', closer);
    });

    function setStyle(s) {
      var _modelValue$value;

      if (((_modelValue$value = modelValue.value) === null || _modelValue$value === void 0 ? void 0 : _modelValue$value.name) === s.name) {
        return;
      }

      emitter.emit('styleSwitched', s);
      map.value.setStyle(s.style);

      if (props.modelValue === undefined) {
        modelValue.value = s;
      }

      ctx.emit('update:modelValue', s);
      toggleOpen(false);
    }

    function toggleOpen(forceIsOpen, e) {
      if (isEvent(e)) {
        e.stopPropagation();
      } else if (isEvent(forceIsOpen)) {
        forceIsOpen.stopPropagation();
      }

      if (props.isOpen !== undefined && props.isOpen === forceIsOpen || isOpen.value === forceIsOpen) {
        return;
      }

      if (props.isOpen === undefined) {
        isOpen.value = typeof forceIsOpen === 'boolean' ? forceIsOpen : !isOpen.value;
        ctx.emit('update:isOpen', isOpen.value);
      } else {
        ctx.emit('update:isOpen', typeof forceIsOpen === 'boolean' ? forceIsOpen : !props.isOpen);
      }
    }

    return {
      isAdded,
      container: control.container,
      setStyle,
      toggleOpen,
      intIsOpen: isOpen,
      intModelValue: modelValue
    };
  },

  // just only for code assist
  template: `
		<div class="maplibregl-ctrl maplibregl-ctrl-group">
		<slot>
			<slot name="button">
				<button type="button" class="maplibregl-ctrl-icon maplibregl-style-switch"></button>
			</slot>
			<slot name="styleList">
				<div class="maplibregl-style-list" style="display: none;">
					<button type="button" class="Dark" data-uri="&quot;mapbox://styles/mapbox/dark-v10&quot;">Dark</button>
					<button type="button" class="Light" data-uri="&quot;mapbox://styles/mapbox/light-v10&quot;">Light</button>
					<button type="button" class="Outdoors" data-uri="&quot;mapbox://styles/mapbox/outdoors-v11&quot;">Outdoors</button>
					<button type="button" class="Satellite" data-uri="&quot;mapbox://styles/mapbox/satellite-streets-v11&quot;">Satellite</button>
					<button type="button" class="Streets active" data-uri="&quot;mapbox://styles/mapbox/streets-v11&quot;">Streets</button>
				</div>
			</slot>
		</slot>
		</div>
	`,

  render() {
    if (!this.isAdded) {
      return createCommentVNode('style-switch-control');
    }

    const slotProps = {
      isOpen: this.intIsOpen,
      currentStyle: this.intModelValue,
      mapStyles: this.mapStyles,
      toggleOpen: this.toggleOpen,
      setStyle: this.setStyle
    };
    return h(Teleport, {
      to: this.container
    }, renderSlot(this.$slots, 'default', slotProps, () => [renderSlot(this.$slots, 'button', slotProps, () => [h(MglButton, {
      type: ButtonType.MDI,
      path: mdiLayersOutline,
      'class': ['maplibregl-ctrl-icon maplibregl-style-switch', this.intIsOpen ? 'is-open' : ''],
      onClick: this.toggleOpen.bind(null, true)
    })]), renderSlot(this.$slots, 'styleList', slotProps, () => [h('div', {
      'class': ['maplibregl-style-list', this.intIsOpen ? 'is-open' : '']
    }, this.mapStyles.map(s => {
      var _this$intModelValue, _this$intModelValue2;

      return s.icon ? h(MglButton, {
        type: ButtonType.MDI,
        path: s.icon.path,
        'class': ((_this$intModelValue = this.intModelValue) === null || _this$intModelValue === void 0 ? void 0 : _this$intModelValue.name) === s.name ? 'is-active' : '',
        onClick: () => this.setStyle(s)
      }, createTextVNode(s.label)) : h('button', {
        type: 'button',
        'class': ((_this$intModelValue2 = this.intModelValue) === null || _this$intModelValue2 === void 0 ? void 0 : _this$intModelValue2.name) === s.name ? 'is-active' : '',
        onClick: () => this.setStyle(s)
      }, createTextVNode(s.label));
    }))])]));
  }

});

var marker_component = defineComponent({
  name: 'MglMarker',
  props: {
    coordinates: {
      type: [Object, Array],
      required: true
    },
    offset: [Object, Array],
    anchor: String,
    color: String,
    // draggable        : Boolean as PropType<boolean>, todo implement feature
    clickTolerance: Number,
    rotation: Number,
    rotationAlignment: String,
    pitchAlignment: String,
    scale: Number
  },

  setup(props) {
    const map = inject(mapSymbol),
          opts = Object.keys(props).filter(opt => props[opt] !== undefined && MapLib.MARKER_OPTION_KEYS.indexOf(opt) !== -1).reduce((obj, opt) => {
      obj[opt] = unref(props[opt]);
      return obj;
    }, {});
    const marker = new Marker(opts);
    marker.setLngLat(props.coordinates).addTo(map.value);
    watch(() => props.coordinates, v => marker.setLngLat(v)); // watch(() => props.draggable, v => marker.setDraggable(v || false));

    watch(() => props.offset, v => marker.setOffset(v || [0, 0]));
    watch(() => props.pitchAlignment, v => marker.setPitchAlignment(v || 'auto'));
    watch(() => props.rotationAlignment, v => marker.setRotationAlignment(v || 'auto'));
    onBeforeUnmount(marker.remove.bind(marker));
    return {
      marker
    };
  },

  render() {// nothing
  }

});

function genSourceOpts(type, props, sourceOpts) {
  return Object.keys(props).filter(opt => props[opt] !== undefined && sourceOpts.indexOf(opt) !== -1).reduce((obj, opt) => {
    obj[opt] = unref(props[opt]);
    return obj;
  }, {
    type
  });
}
const refs = new Map();
function getSourceRef(mcid, source) {
  const isString = typeof source === 'string',
        key = String(mcid) + (isString ? source : '');
  let r = refs.get(key);

  if (!r) {
    r = ref(isString ? null : undefined);
    refs.set(key, r);
  }

  return r;
}
function bindSource(map, source, isLoaded, emitter, props, type, sourceOpts, registry) {
  function addSource() {
    if (isLoaded.value) {
      map.value.addSource(props.sourceId, genSourceOpts(type, props, sourceOpts));
      source.value = map.value.getSource(props.sourceId);
    }
  }

  function resetSource() {
    source.value = null;
  }

  watch(isLoaded, addSource, {
    immediate: true
  });
  map.value.on('style.load', addSource);
  emitter.on('styleSwitched', resetSource);
  return onBeforeUnmount(() => {
    if (isLoaded.value) {
      registry.unmount();
      map.value.removeSource(props.sourceId);
    }

    map.value.off('style.load', addSource);
    emitter.off('styleSwitched', resetSource);
  });
}

class SourceLayerRegistry {
  constructor() {
    _defineProperty(this, "unmountHandlers", new Map());
  }

  registerUnmountHandler(id, handler) {
    this.unmountHandlers.set(id, handler);
  }

  unregisterUnmountHandler(id) {
    this.unmountHandlers.delete(id);
  }

  unmount() {
    this.unmountHandlers.forEach(h => h());
  }

}

const sourceOpts$7 = ['animate', 'coordinates', 'canvas'];
var canvas_source = defineComponent({
  name: 'MglCanvasSource',
  props: {
    sourceId: {
      type: String,
      required: true
    },
    coordinates: Array,
    animate: Boolean,
    canvas: [HTMLCanvasElement, String]
  },

  setup(props) {
    const map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          emitter = inject(emitterSymbol),
          cid = inject(componentIdSymbol),
          source = getSourceRef(cid, props.sourceId),
          registry = new SourceLayerRegistry();
    provide(sourceIdSymbol, props.sourceId);
    provide(sourceLayerRegistry, registry);
    bindSource(map, source, isLoaded, emitter, props, 'canvas', sourceOpts$7, registry);
    watch(() => props.coordinates, v => {
      var _source$value;

      return (_source$value = source.value) === null || _source$value === void 0 ? void 0 : _source$value.setCoordinates(v || []);
    });
    return {
      source
    };
  },

  render() {
    return [createCommentVNode('Canvas Source'), this.source && this.$slots.default ? this.$slots.default() : undefined];
  }

});

const sourceOpts$6 = ['data', 'maxzoom', 'attribution', 'buffer', 'tolerance', 'cluster', 'clusterRadius', 'clusterMaxZoom', 'clusterMinPoints', 'clusterProperties', 'lineMetrics', 'generateId', 'promoteId', 'filter'];
var geojson_source = defineComponent({
  name: 'MglGeoJsonSource',
  props: {
    sourceId: {
      type: String,
      required: true
    },
    data: [Object, String],
    maxzoom: Number,
    attribution: String,
    buffer: Number,
    tolerance: Number,
    cluster: [Number, Boolean],
    clusterRadius: Number,
    clusterMaxZoom: Number,
    clusterMinPoints: Number,
    clusterProperties: Object,
    lineMetrics: Boolean,
    generateId: Boolean,
    promoteId: Object,
    filter: [Array, String, Object]
  },

  setup(props) {
    const map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          emitter = inject(emitterSymbol),
          cid = inject(componentIdSymbol),
          source = getSourceRef(cid, props.sourceId),
          registry = new SourceLayerRegistry();
    provide(sourceIdSymbol, props.sourceId);
    provide(sourceLayerRegistry, registry);
    bindSource(map, source, isLoaded, emitter, props, 'geojson', sourceOpts$6, registry);
    watch(isRef(props.data) ? props.data : () => props.data, v => {
      var _source$value;

      return (_source$value = source.value) === null || _source$value === void 0 ? void 0 : _source$value.setData(v || {
        type: 'FeatureCollection',
        features: []
      });
    });
    return {
      source
    };
  },

  render() {
    return [createCommentVNode('GeoJSON Source'), this.source && this.$slots.default ? this.$slots.default() : undefined];
  }

});

const sourceOpts$5 = ['url', 'coordinates'];
var image_source = defineComponent({
  name: 'MglImageSource',
  props: {
    sourceId: {
      type: String,
      required: true
    },
    url: String,
    coordinates: Array
  },

  setup(props) {
    const map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          emitter = inject(emitterSymbol),
          cid = inject(componentIdSymbol),
          source = getSourceRef(cid, props.sourceId),
          registry = new SourceLayerRegistry();
    provide(sourceIdSymbol, props.sourceId);
    provide(sourceLayerRegistry, registry);
    bindSource(map, source, isLoaded, emitter, props, 'image', sourceOpts$5, registry);
    watch(() => props.coordinates, v => {
      var _source$value;

      return (_source$value = source.value) === null || _source$value === void 0 ? void 0 : _source$value.setCoordinates(v || []);
    });
    return {
      source
    };
  },

  render() {
    return [createCommentVNode('Image Source'), this.source && this.$slots.default ? this.$slots.default() : undefined];
  }

});

const sourceOpts$4 = ['url', 'tiles', 'bounds', 'minzoom', 'maxzoom', 'tileSize', 'scheme', 'attribution'];
var raster_source = defineComponent({
  name: 'MglRasterSource',
  props: {
    sourceId: {
      type: String,
      required: true
    },
    url: String,
    tiles: Array,
    bounds: Array,
    minzoom: Number,
    maxzoom: Number,
    tileSize: Number,
    scheme: String,
    attribution: String
  },

  setup(props) {
    const map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          emitter = inject(emitterSymbol),
          cid = inject(componentIdSymbol),
          source = getSourceRef(cid, props.sourceId),
          registry = new SourceLayerRegistry();
    provide(sourceIdSymbol, props.sourceId);
    provide(sourceLayerRegistry, registry);
    bindSource(map, source, isLoaded, emitter, props, 'raster', sourceOpts$4, registry);
    return {
      source
    };
  },

  render() {
    return createCommentVNode('Video Source');
  }

});

const sourceOpts$3 = ['url', 'tiles', 'bounds', 'minzoom', 'maxzoom', 'tileSize', 'attribution', 'encoding'];
var rasterDem_source = defineComponent({
  name: 'MglRasterDemSource',
  props: {
    sourceId: {
      type: String,
      required: true
    },
    url: String,
    tiles: Array,
    bounds: Array,
    minzoom: Number,
    maxzoom: Number,
    tileSize: Number,
    attribution: String,
    encoding: String
  },

  setup(props) {
    const map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          emitter = inject(emitterSymbol),
          cid = inject(componentIdSymbol),
          source = getSourceRef(cid, props.sourceId),
          registry = new SourceLayerRegistry();
    provide(sourceIdSymbol, props.sourceId);
    provide(sourceLayerRegistry, registry);
    bindSource(map, source, isLoaded, emitter, props, 'raster-dem', sourceOpts$3, registry);
    return {
      source
    };
  },

  render() {
    return createCommentVNode('Video Source');
  }

});

const sourceOpts$2 = ['url', 'tiles', 'bounds', 'scheme', 'minzoom', 'maxzoom', 'attribution', 'promoteId'];
var vector_source = defineComponent({
  name: 'MglVectorSource',
  props: {
    sourceId: {
      type: String,
      required: true
    },
    url: String,
    tiles: Array,
    bounds: Array,
    scheme: String,
    minzoom: Number,
    maxzoom: Number,
    attribution: String,
    promoteId: Object
  },

  setup(props) {
    const map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          emitter = inject(emitterSymbol),
          cid = inject(componentIdSymbol),
          source = getSourceRef(cid, props.sourceId),
          registry = new SourceLayerRegistry();
    provide(sourceIdSymbol, props.sourceId);
    provide(sourceLayerRegistry, registry);
    bindSource(map, source, isLoaded, emitter, props, 'vector', sourceOpts$2, registry);
    watch(() => props.tiles, v => {
      var _source$value;

      return (_source$value = source.value) === null || _source$value === void 0 ? void 0 : _source$value.setTiles(v || []);
    });
    watch(() => props.url, v => {
      var _source$value2;

      return (_source$value2 = source.value) === null || _source$value2 === void 0 ? void 0 : _source$value2.setUrl(v || '');
    });
    return {
      source
    };
  },

  render() {
    return createCommentVNode('Vector Source');
  }

});

const sourceOpts$1 = ['urls', 'coordinates'];
var video_source = defineComponent({
  name: 'MglVideoSource',
  props: {
    sourceId: {
      type: String,
      required: true
    },
    urls: Array,
    coordinates: Array
  },

  setup(props) {
    const map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          emitter = inject(emitterSymbol),
          cid = inject(componentIdSymbol),
          source = getSourceRef(cid, props.sourceId),
          registry = new SourceLayerRegistry();
    provide(sourceIdSymbol, props.sourceId);
    provide(sourceLayerRegistry, registry);
    bindSource(map, source, isLoaded, emitter, props, 'video', sourceOpts$1, registry);
    watch(() => props.coordinates, v => {
      var _source$value;

      return (_source$value = source.value) === null || _source$value === void 0 ? void 0 : _source$value.setCoordinates(v || []);
    });
    return {
      source
    };
  },

  render() {
    return createCommentVNode('Video Source');
  }

});

const sourceOpts = ['metadata', 'ref', 'source', 'sourceLayer', 'minzoom', 'maxzoom', 'interactive', 'filter', 'layout', 'paint'];
const layerEvents = ['click', 'dblclick', 'mousedown', 'mouseup', 'mousemove', 'mouseenter', 'mouseleave', 'mouseover', 'mouseout', 'contextmenu', 'touchstart', 'touchend', 'touchcancel'];
const Shared = defineComponent({
  props: {
    layerId: {
      type: String,
      required: true
    },
    source: [String, Object],
    metadata: [Object, Array, String, Number],
    ref: String,
    sourceLayer: String,
    minzoom: Number,
    maxzoom: Number,
    interactive: Boolean,
    filter: Array,
    before: String
  },
  emit: ['click', 'dblclick', 'mousedown', 'mouseup', 'mousemove', 'mouseenter', 'mouseleave', 'mouseover', 'mouseout', 'contextmenu', 'touchstart', 'touchend', 'touchcancel']
});
function genLayerOpts(id, type, props, source) {
  return Object.keys(props).filter(opt => props[opt] !== undefined && sourceOpts.indexOf(opt) !== -1).reduce((obj, opt) => {
    obj[opt === 'sourceLayer' ? 'source-layer' : opt] = unref(props[opt]);
    return obj;
  }, {
    type,
    source: props.source || source,
    id
  });
}
function registerLayerEvents(map, layerId, vn) {
  if (!vn.props) {
    return;
  }

  for (let i = 0, len = layerEvents.length; i < len; i++) {
    const evProp = 'on' + layerEvents[i].charAt(0).toUpperCase() + layerEvents[i].substr(1);

    if (vn.props[evProp]) {
      map.on(layerEvents[i], layerId, vn.props[evProp]);
    }
  }
}
function unregisterLayerEvents(map, layerId, vn) {
  if (!vn.props) {
    return;
  }

  for (let i = 0, len = layerEvents.length; i < len; i++) {
    const evProp = 'on' + layerEvents[i].charAt(0).toUpperCase() + layerEvents[i].substr(1);

    if (vn.props[evProp]) {
      map.off(layerEvents[i], layerId, vn.props[evProp]);
    }
  }
}
function handleDispose(isLoaded, map, ci, props, registry) {
  function removeLayer() {
    if (isLoaded.value) {
      unregisterLayerEvents(map.value, props.layerId, ci.vnode);
      const layer = map.value.getLayer(props.layerId);

      if (layer) {
        map.value.removeLayer(props.layerId);
      }
    }
  }

  registry.registerUnmountHandler(props.layerId, removeLayer);
  onBeforeUnmount(() => {
    registry.unregisterUnmountHandler(props.layerId);
    removeLayer();
  });
}

var background_layer = defineComponent({
  name: 'MglBackgroundLayer',
  mixins: [Shared],
  props: {
    layout: Object,
    paint: Object
  },

  setup(props) {
    const sourceId = inject(sourceIdSymbol);

    if (!sourceId && !props.source) {
      warn('Background Layer: layer must be used inside source tag or source prop must be set');
      return;
    }

    const map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          cid = inject(componentIdSymbol),
          registry = inject(sourceLayerRegistry),
          sourceRef = getSourceRef(cid, props.source || sourceId);
    watch([isLoaded, sourceRef], ([il, src]) => {
      if (il && (src || src === undefined)) {
        map.value.addLayer(genLayerOpts(props.layerId, 'background', props, sourceId), props.before || undefined);
      }
    }, {
      immediate: true
    });

    function removeLayer() {
      if (isLoaded.value) {
        map.value.removeLayer(props.layerId);
      }
    }

    registry.registerUnmountHandler(props.layerId, removeLayer);
    onBeforeUnmount(() => {
      removeLayer();
    });
  },

  render() {
    return createCommentVNode('Background Layer');
  }

});

var circle_layer = defineComponent({
  name: 'MglCircleLayer',
  mixins: [Shared],
  props: {
    layout: Object,
    paint: Object
  },

  setup(props) {
    const sourceId = inject(sourceIdSymbol);

    if (!sourceId && !props.source) {
      warn('Circle Layer: layer must be used inside source tag or source prop must be set');
      return;
    }

    const ci = getCurrentInstance(),
          map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          cid = inject(componentIdSymbol),
          registry = inject(sourceLayerRegistry),
          sourceRef = getSourceRef(cid, props.source || sourceId);
    watch([isLoaded, sourceRef], ([il, src]) => {
      if (il && (src || src === undefined)) {
        map.value.addLayer(genLayerOpts(props.layerId, 'circle', props, sourceId), props.before || undefined);
        registerLayerEvents(map.value, props.layerId, ci.vnode);
      }
    }, {
      immediate: true
    });
    handleDispose(isLoaded, map, ci, props, registry);
  },

  render() {
    return createCommentVNode('Circle Layer');
  }

});

var fill_layer = defineComponent({
  name: 'MglFillLayer',
  mixins: [Shared],
  props: {
    layout: Object,
    paint: Object
  },

  setup(props) {
    const sourceId = inject(sourceIdSymbol);

    if (!sourceId && !props.source) {
      warn('Fill Layer: layer must be used inside source tag or source prop must be set');
      return;
    }

    const ci = getCurrentInstance(),
          map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          cid = inject(componentIdSymbol),
          registry = inject(sourceLayerRegistry),
          sourceRef = getSourceRef(cid, props.source || sourceId);
    watch([isLoaded, sourceRef], ([il, src]) => {
      if (il && (src || src === undefined)) {
        map.value.addLayer(genLayerOpts(props.layerId, 'fill', props, sourceId), props.before || undefined);
        registerLayerEvents(map.value, props.layerId, ci.vnode);
      }
    }, {
      immediate: true
    });
    handleDispose(isLoaded, map, ci, props, registry);
  },

  render() {
    return createCommentVNode('Fill Layer');
  }

});

var fillExtrusion_layer = defineComponent({
  name: 'MglFillExtrusionLayer',
  mixins: [Shared],
  props: {
    layout: Object,
    paint: Object
  },

  setup(props) {
    const sourceId = inject(sourceIdSymbol);

    if (!sourceId && !props.source) {
      warn('Fill Extrude Layer: layer must be used inside source tag or source prop must be set');
      return;
    }

    const ci = getCurrentInstance(),
          map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          cid = inject(componentIdSymbol),
          registry = inject(sourceLayerRegistry),
          sourceRef = getSourceRef(cid, props.source || sourceId);
    watch([isLoaded, sourceRef], ([il, src]) => {
      if (il && (src || src === undefined)) {
        map.value.addLayer(genLayerOpts(props.layerId, 'fill-extrusion', props, sourceId), props.before || undefined);
        registerLayerEvents(map.value, props.layerId, ci.vnode);
      }
    }, {
      immediate: true
    });
    handleDispose(isLoaded, map, ci, props, registry);
  },

  render() {
    return createCommentVNode('Fill Extrusion Layer');
  }

});

var heatmap_layer = defineComponent({
  name: 'MglHeatmapLayer',
  mixins: [Shared],
  props: {
    layout: Object,
    paint: Object
  },

  setup(props) {
    const sourceId = inject(sourceIdSymbol);

    if (!sourceId && !props.source) {
      warn('Heatmap Layer: layer must be used inside source tag or source prop must be set');
      return;
    }

    const ci = getCurrentInstance(),
          map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          cid = inject(componentIdSymbol),
          registry = inject(sourceLayerRegistry),
          sourceRef = getSourceRef(cid, props.source || sourceId);
    watch([isLoaded, sourceRef], ([il, src]) => {
      if (il && (src || src === undefined)) {
        map.value.addLayer(genLayerOpts(props.layerId, 'heatmap', props, sourceId), props.before || undefined);
        registerLayerEvents(map.value, props.layerId, ci.vnode);
      }
    }, {
      immediate: true
    });
    handleDispose(isLoaded, map, ci, props, registry);
  },

  render() {
    return createCommentVNode('Heatmap Layer');
  }

});

var hillshade_layer = defineComponent({
  name: 'MglHillshadeLayer',
  mixins: [Shared],
  props: {
    layout: Object,
    paint: Object
  },

  setup(props) {
    const sourceId = inject(sourceIdSymbol);

    if (!sourceId && !props.source) {
      warn('Hillshade Layer: layer must be used inside source tag or source prop must be set');
      return;
    }

    const ci = getCurrentInstance(),
          map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          cid = inject(componentIdSymbol),
          registry = inject(sourceLayerRegistry),
          sourceRef = getSourceRef(cid, props.source || sourceId);
    watch([isLoaded, sourceRef], ([il, src]) => {
      if (il && (src || src === undefined)) {
        map.value.addLayer(genLayerOpts(props.layerId, 'hillshade', props, sourceId), props.before || undefined);
        registerLayerEvents(map.value, props.layerId, ci.vnode);
      }
    }, {
      immediate: true
    });
    handleDispose(isLoaded, map, ci, props, registry);
  },

  render() {
    return createCommentVNode('Hillshade Layer');
  }

});

var line_layer = defineComponent({
  name: 'MglLineLayer',
  mixins: [Shared],
  props: {
    layout: Object,
    paint: Object
  },

  setup(props) {
    const sourceId = inject(sourceIdSymbol);

    if (!sourceId && !props.source) {
      warn('Line Layer: layer must be used inside source tag or source prop must be set');
      return;
    }

    const ci = getCurrentInstance(),
          map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          cid = inject(componentIdSymbol),
          registry = inject(sourceLayerRegistry),
          sourceRef = getSourceRef(cid, props.source || sourceId);
    watch([isLoaded, sourceRef], ([il, src]) => {
      if (il && (src || src === undefined)) {
        map.value.addLayer(genLayerOpts(props.layerId, 'line', props, sourceId), props.before || undefined);
        registerLayerEvents(map.value, props.layerId, ci.vnode);
      }
    }, {
      immediate: true
    });
    handleDispose(isLoaded, map, ci, props, registry);
  },

  render() {
    return createCommentVNode('Line Layer');
  }

});

var raster_layer = defineComponent({
  name: 'MglRasterLayer',
  mixins: [Shared],
  props: {
    layout: Object,
    paint: Object
  },

  setup(props) {
    const sourceId = inject(sourceIdSymbol);

    if (!sourceId && !props.source) {
      warn('Raster Layer: layer must be used inside source tag or source prop must be set');
      return;
    }

    const ci = getCurrentInstance(),
          map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          cid = inject(componentIdSymbol),
          registry = inject(sourceLayerRegistry),
          sourceRef = getSourceRef(cid, props.source || sourceId);
    watch([isLoaded, sourceRef], ([il, src]) => {
      if (il && (src || src === undefined)) {
        map.value.addLayer(genLayerOpts(props.layerId, 'raster', props, sourceId), props.before || undefined);
        registerLayerEvents(map.value, props.layerId, ci.vnode);
      }
    }, {
      immediate: true
    });
    handleDispose(isLoaded, map, ci, props, registry);
  },

  render() {
    return createCommentVNode('Raster Layer');
  }

});

var smybol_layer = defineComponent({
  name: 'MglSymbolLayer',
  mixins: [Shared],
  props: {
    layout: Object,
    paint: Object
  },

  setup(props) {
    const sourceId = inject(sourceIdSymbol);

    if (!sourceId && !props.source) {
      warn('Symbol Layer: layer must be used inside source tag or source prop must be set');
      return;
    }

    const ci = getCurrentInstance(),
          map = inject(mapSymbol),
          isLoaded = inject(isLoadedSymbol),
          cid = inject(componentIdSymbol),
          registry = inject(sourceLayerRegistry),
          sourceRef = getSourceRef(cid, props.source || sourceId);
    watch([isLoaded, sourceRef], ([il, src]) => {
      if (il && (src || src === undefined)) {
        map.value.addLayer(genLayerOpts(props.layerId, 'symbol', props, sourceId), props.before || undefined);
        registerLayerEvents(map.value, props.layerId, ci.vnode);
      }
    }, {
      immediate: true
    });
    handleDispose(isLoaded, map, ci, props, registry);
  },

  render() {
    return createCommentVNode('Symbol Layer');
  }

});

/* eslint-disable import/prefer-default-export */

var components = /*#__PURE__*/Object.freeze({
	__proto__: null,
	MglMap: map_component,
	MglAttributionControl: attribution_control,
	MglCustomControl: custom_control,
	MglFullscreenControl: fullscreen_control,
	MglFrameRateControl: frameRate_control,
	MglGeolocationControl: geolocation_control,
	MglNavigationControl: navigation_control,
	MglScaleControl: scale_control,
	MglStyleSwitchControl: styleSwitch_control,
	MglButton: MglButton,
	MglMarker: marker_component,
	MglCanvasSource: canvas_source,
	MglGeoJsonSource: geojson_source,
	MglImageSource: image_source,
	MglRasterSource: raster_source,
	MglRasterDemSource: rasterDem_source,
	MglVectorSource: vector_source,
	MglVideoSource: video_source,
	MglBackgroundLayer: background_layer,
	MglCircleLayer: circle_layer,
	MglFillLayer: fill_layer,
	MglFillExtrusionLayer: fillExtrusion_layer,
	MglHeatmapLayer: heatmap_layer,
	MglHillshadeLayer: hillshade_layer,
	MglLineLayer: line_layer,
	MglRasterLayer: raster_layer,
	MglSymbolLayer: smybol_layer
});

// Import vue components

const install = function installVueMaplibreGl(app) {
  Object.entries(components).forEach(([componentName, component]) => {
    app.component(componentName, component);
  });
}; // Create module definition for Vue.use()

export { attribution_control as MglAttributionControl, background_layer as MglBackgroundLayer, MglButton, canvas_source as MglCanvasSource, circle_layer as MglCircleLayer, custom_control as MglCustomControl, defaults as MglDefaults, fillExtrusion_layer as MglFillExtrusionLayer, fill_layer as MglFillLayer, frameRate_control as MglFrameRateControl, fullscreen_control as MglFullscreenControl, geojson_source as MglGeoJsonSource, geolocation_control as MglGeolocationControl, heatmap_layer as MglHeatmapLayer, hillshade_layer as MglHillshadeLayer, image_source as MglImageSource, line_layer as MglLineLayer, map_component as MglMap, marker_component as MglMarker, navigation_control as MglNavigationControl, rasterDem_source as MglRasterDemSource, raster_layer as MglRasterLayer, raster_source as MglRasterSource, scale_control as MglScaleControl, styleSwitch_control as MglStyleSwitchControl, smybol_layer as MglSymbolLayer, vector_source as MglVectorSource, video_source as MglVideoSource, Position, componentIdSymbol, install as default, emitterSymbol, isLoadedSymbol, mapSymbol, sourceIdSymbol, sourceLayerRegistry, useMap, usePositionWatcher };
